import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import { tourConfig, isTourAvailable } from './tourConfig';
import { TOUR_ACTIONS } from './tourConstants';
import { resolveTourSelector, waitForElement } from './tourHelpers';
import { updateUserTours } from '../services/backendAPI';

// allowClose: false drops "close" from driver.js's global showButtons,
// so each step lists the buttons itself to keep Skip visible.
const STEP_BUTTONS = ['next', 'previous', 'close'];

class TourManager {
  constructor() {
    this.driverInstance = null; // set only while a tour is running
    this.tourId = null;
    this.tourPath = null; // the page the tour is showing; leaving it ends the tour
    this.dependencies = null; // { navigate, broadcastIntent, onStop }
    this.isPreparingStep = false;
    this.resizeTimeout = null;
    // Tours finished, skipped or left this session; not offered again until the next login
    this.closedTourIds = new Set();
    this.handleResize = this.handleResize.bind(this);
  }

  // Returns false when the tour can't start (one is already running, or this build doesn't have it)
  start(tourId, dependencies) {
    if (this.driverInstance || !isTourAvailable(tourId)) return false;

    const driverInstance = driver({
      showProgress: true,
      animate: true,
      smoothScroll: false, // prepareStep has already scrolled the target into place
      duration: 250,
      popoverOffset: 15,
      allowClose: false, // Escape and overlay clicks must not end the tour
      overlayColor: 'rgba(0, 0, 0, 0.7)',
      nextBtnText: 'Next →',
      prevBtnText: '← Back',
      doneBtnText: 'Finish',
      onNextClick: () => this.move(1),
      onPrevClick: () => this.move(-1),
      onDoneClick: () => this.completeAndStop(),
      onCloseClick: () => this.completeAndStop(),
      onDestroyStarted: () => this.stop(),
      onPopoverRender: (popover) => {
        popover.closeButton.textContent = 'Skip';
        popover.closeButton.setAttribute('aria-label', 'Skip tour');
      },
      steps: tourConfig[tourId].steps.map((step) => ({
        element: resolveTourSelector(step.target),
        popover: { title: step.title, description: step.description, showButtons: STEP_BUTTONS },
      })),
    });
    this.driverInstance = driverInstance;
    this.tourId = tourId;
    this.tourPath = window.location.pathname;
    this.dependencies = dependencies;
    window.addEventListener('resize', this.handleResize);

    this.prepareStep(0)
      .then((isActive) => {
        if (isActive) driverInstance.drive();
      })
      .catch((error) => {
        console.error(`TourManager: Error starting tour ${tourId}:`, error);
        this.stop();
      });
    return true;
  }

  stop() {
    const { driverInstance, dependencies } = this;
    if (!driverInstance) return;

    this.driverInstance = null;
    this.tourId = null;
    this.tourPath = null;
    this.dependencies = null;
    window.removeEventListener('resize', this.handleResize);
    clearTimeout(this.resizeTimeout);
    try {
      driverInstance.destroy();
    } catch (error) {
      console.error('TourManager: Error destroying driver instance', error);
    }
    dependencies.onStop?.();
  }

  // Next/Back: prepare the page first, then let driver.js move the spotlight
  async move(offset) {
    const driverInstance = this.driverInstance;
    if (!driverInstance || this.isPreparingStep) return;
    const index = driverInstance.getActiveIndex() + offset;
    if (await this.prepareStep(index)) driverInstance.moveTo(index);
  }

  // Runs the step's actions, waits for its target and centres it. Resolves false if
  // the step doesn't exist or the tour was stopped meanwhile (e.g. Skip during the wait).
  async prepareStep(index) {
    const driverInstance = this.driverInstance;
    const step = tourConfig[this.tourId]?.steps[index];
    if (!step) return false;

    this.isPreparingStep = true;
    try {
      [].concat(step.action ?? []).forEach((action) => this.runAction(action));
      const element = await waitForElement(resolveTourSelector(step.target));
      if (this.driverInstance !== driverInstance) return false;

      // Instant centring leaves room for the popover on small screens. A missing target
      // falls back to driver.js showing the popover in the middle of the screen.
      if (element) element.scrollIntoView({ block: 'center' });
      else console.warn(`TourManager: Could not find tour target "${step.target}"`);
      return true;
    } finally {
      this.isPreparingStep = false;
    }
  }

  runAction(action) {
    const { navigate, broadcastIntent } = this.dependencies;
    if (action.type === TOUR_ACTIONS.NAVIGATE) {
      // Record the page first, so the route change this causes doesn't end the tour.
      // Skip navigating when already there, so Back to the first step adds no history entry.
      this.tourPath = action.path;
      if (window.location.pathname !== action.path) navigate(action.path);
    } else {
      // A fresh object each time, so a repeated intent still reaches the page
      broadcastIntent({ ...action });
    }
  }

  // driver.js refreshes on resize straight away, but the Relationship Manager card animates
  // its size (transition: all 0.2s), so refresh once more after it settles
  handleResize() {
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(() => this.driverInstance?.refresh(), 250);
  }

  completeAndStop() {
    if (!this.tourId) return;
    this.markCompleted(this.tourId);
    this.stop();
  }

  // Leaving the tour's page (e.g. browser Back) ends the tour. It isn't an explicit Skip,
  // so the backend isn't told: the tour is offered again at the next login, not this session.
  handleRouteChange(pathname) {
    if (!this.driverInstance || pathname === this.tourPath) return;
    this.closedTourIds.add(this.tourId);
    this.stop();
  }

  // Not awaited, so closing never waits on the network. primusAPI can resolve with status "error"
  // on HTTP 200, so that counts as a failure; on failure the backend flag stays true and the
  // tour is offered again at the next login.
  markCompleted(tourId) {
    if (this.closedTourIds.has(tourId)) return;
    this.closedTourIds.add(tourId);
    updateUserTours({ tours: { [tourId]: false } })
      .then((response) => {
        if (response?.status === 'error') throw response.message;
      })
      .catch((error) => console.error(`TourManager: Failed to mark tour ${tourId} as completed:`, error));
  }
}

export const tourManager = new TourManager();
